import { useState } from "react";
import type { FormEvent } from "react";
import Reveal from "@/components/base/Reveal";
import { contactCategories, contactNotes, googleFormUrl } from "@/mocks/contact";

// 質問フォームの送信先（フォーム機能で作成した「留学に関する質問フォーム」）
const FORM_ENDPOINT = "https://readdy.ai/api/form/dbfdc0g4rrdm9pkr0drg";

type SubmitStatus = "idle" | "submitting" | "success" | "error";

const fieldClass =
  "mt-2 box-border w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-900 transition-colors placeholder:text-foreground-400 focus:border-primary-500";

const labelClass = "block text-[12.5px] font-semibold text-foreground-800";

export default function ContactSection() {
  const [replyWanted, setReplyWanted] = useState(false);
  const [status, setStatus] = useState<SubmitStatus>("idle");
  const [statusMessage, setStatusMessage] = useState("");
  const [questionLength, setQuestionLength] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setStatusMessage("質問を受け付けました。内容を確認のうえ、記事作成の参考にさせていただきます。");
      form.reset();
      setReplyWanted(false);
      setQuestionLength(0);
      return;
    }
    formData.delete("website_alt");

    const question = String(formData.get("message") ?? "").trim();
    const email = String(formData.get("email") ?? "").trim();
    const inquiryType = String(formData.get("inquiry_type") ?? "").trim();

    if (!inquiryType) {
      setStatus("error");
      setStatusMessage("お問い合わせの種類を選択してください。");
      return;
    }
    if (!question) {
      setStatus("error");
      setStatusMessage("質問・お問い合わせ内容を入力してください。");
      return;
    }
    if (replyWanted && !email) {
      setStatus("error");
      setStatusMessage("返信を希望される場合は、メールアドレスを入力してください。");
      return;
    }
    if (replyWanted && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("error");
      setStatusMessage("メールアドレスの形式を確認してください。");
      return;
    }

    setStatus("submitting");
    setStatusMessage("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string" && value.trim() !== "") {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || "";
      const looksLikeSpam = /spam/i.test(serverMessage) || /spam/i.test(responseText);
      const isSuccess = response.ok && parsed?.code === "OK" && !looksLikeSpam;

      if (!isSuccess) {
        setStatus("error");
        setStatusMessage(
          serverMessage || "送信できませんでした。時間をおいて、もう一度お試しください。",
        );
        return;
      }

      setStatus("success");
      setStatusMessage(
        "質問を受け付けました。内容を確認のうえ、記事作成の参考にさせていただきます。",
      );
      form.reset();
      setReplyWanted(false);
      setQuestionLength(0);
    } catch {
      setStatus("error");
      setStatusMessage(
        "通信エラーが発生しました。電波の良い環境で、もう一度お試しください。",
      );
    }
  };

  return (
    <section
      id="contact"
      className="w-full scroll-mt-[88px] border-t border-background-200 bg-background-100 md:scroll-mt-[104px]"
    >
      <div className="mx-auto w-full max-w-content px-5 py-16 md:px-6 md:py-20">
        <div className="grid gap-10 lg:grid-cols-[minmax(0,0.85fr)_minmax(0,1.15fr)] lg:gap-14">
          <Reveal>
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.22em] text-primary-700">
              <span className="h-[1px] w-6 bg-primary-500" />
              Contact
            </p>
            <h2 className="mt-3 font-heading text-[26px] font-bold leading-[1.5] tracking-tight text-foreground-950 md:text-[32px]">
              留学について質問がありますか？
            </h2>
            <p className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-primary-100 px-3 py-1 text-[11px] font-semibold text-primary-800">
              <i className="ri-price-tag-3-line text-[12px]" />
              質問・お問い合わせは無料です
            </p>
            <p className="mt-4 text-[13px] leading-[1.95] text-foreground-700 md:text-[14px]">
              コミカレへの出願、F-1ビザ、TSI、留学費用、学生生活などについて、気になることがあればお気軽にご質問ください。いただいた質問は、今後の記事作成の参考にもさせていただきます。
            </p>

            <ul className="mt-6 space-y-2.5">
              {contactNotes.map((note) => (
                <li
                  key={note}
                  className="flex items-start gap-2.5 text-[12px] leading-[1.9] text-foreground-600"
                >
                  <i className="ri-check-line mt-[4px] shrink-0 text-[14px] text-primary-600" />
                  {note}
                </li>
              ))}
            </ul>

            <p className="mt-5 flex items-start gap-2 rounded-lg border border-background-200 bg-background-50 px-4 py-3 text-[11.5px] leading-[1.85] text-foreground-500">
              <i className="ri-information-line mt-[2px] shrink-0 text-[13px] text-primary-600" />
              すべての質問への個別回答を保証するものではありません。ビザ申請や大学出願に関する最終的な判断は、各公式機関の最新情報をご確認ください。
            </p>
          </Reveal>

          <Reveal delay={80}>
            <div className="rounded-[12px] border border-background-200 bg-background-50 p-6 shadow-[0_18px_40px_-32px_rgba(23,37,35,0.35)] md:p-8">
              {googleFormUrl ? (
                <div>
                  <div className="flex flex-wrap items-center justify-between gap-2.5">
                    <p className="font-heading text-[16px] font-bold text-foreground-950">
                      無料で質問する
                    </p>
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-100 px-2.5 py-1 text-[10.5px] font-bold text-primary-800">
                      <i className="ri-check-line text-[11px]" />
                      質問は完全無料
                    </span>
                  </div>
                  <p className="mt-2.5 text-[12.5px] leading-[1.9] text-foreground-600">
                    下のボタンを押すと、Googleフォームの質問フォームが<strong className="font-semibold text-foreground-800">新しいタブ</strong>で開きます。入力内容は運営者のみが確認します。
                  </p>
                  <a
                    href={googleFormUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-5 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3.5 text-[13.5px] font-semibold text-background-50 shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:bg-accent-600 hover:shadow-md"
                  >
                    <i className="ri-edit-box-line text-[15px]" />
                    無料で質問する
                    <i className="ri-external-link-line text-[13px]" />
                  </a>
                  <p className="mt-3 text-center text-[11px] leading-[1.8] text-foreground-400">
                    Googleフォーム（外部サイト）が別タブで開きます。しつこい営業や有料相談の勧誘はありません。
                  </p>
                </div>
              ) : (
                <form data-readdy-form onSubmit={handleSubmit} noValidate>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                    className="contact-alt-field"
                  />

                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="contact-type" className={labelClass}>
                      お問い合わせの種類
                      <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-[1px] text-[10px] font-semibold text-primary-800">
                        必須
                      </span>
                    </label>
                    <select id="contact-type" name="inquiry_type" required className={fieldClass} defaultValue="">
                      <option value="" disabled>
                        選択してください
                      </option>
                      {contactCategories.map((category) => (
                        <option key={category} value={category}>
                          {category}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-5 flex flex-col gap-1.5">
                    <div className="flex items-center justify-between gap-3">
                      <label htmlFor="contact-message" className={labelClass}>
                        質問・お問い合わせ内容
                        <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-[1px] text-[10px] font-semibold text-primary-800">
                          必須
                        </span>
                      </label>
                      <span className="text-[11px] text-foreground-400">{questionLength} / 500</span>
                    </div>
                    <textarea
                      id="contact-message"
                      name="message"
                      rows={5}
                      required
                      maxLength={500}
                      placeholder="例：コミカレの出願に必要な書類は、どのくらい前から集めましたか？"
                      className={`${fieldClass} resize-y leading-[1.9]`}
                      onChange={(event) => setQuestionLength(event.target.value.length)}
                    />
                  </div>

                  <fieldset className="mt-5 border-0 p-0">
                    <legend className={labelClass}>返信を希望しますか（任意）</legend>
                    <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-5">
                      <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-foreground-700">
                        <input
                          type="radio"
                          name="reply_request"
                          value="返信を希望する"
                          checked={replyWanted}
                          onChange={() => setReplyWanted(true)}
                          className="h-4 w-4 accent-primary-600"
                        />
                        返信を希望する
                      </label>
                      <label className="flex cursor-pointer items-center gap-2 text-[12.5px] text-foreground-700">
                        <input
                          type="radio"
                          name="reply_request"
                          value="返信は不要"
                          checked={!replyWanted}
                          onChange={() => setReplyWanted(false)}
                          className="h-4 w-4 accent-primary-600"
                        />
                        返信は不要
                      </label>
                    </div>
                  </fieldset>

                  {replyWanted && (
                    <div className="mt-5 flex flex-col gap-1.5">
                      <label htmlFor="contact-email" className={labelClass}>
                        メールアドレス
                        <span className="ml-1.5 rounded-full bg-primary-100 px-1.5 py-[1px] text-[10px] font-semibold text-primary-800">
                          必須
                        </span>
                      </label>
                      <input
                        id="contact-email"
                        type="email"
                        name="email"
                        required={replyWanted}
                        autoComplete="email"
                        placeholder="you@example.com"
                        className={fieldClass}
                      />
                      <p className="mt-1 text-[11px] leading-[1.7] text-foreground-400">
                        返信にのみ使用します。第三者へ共有することはありません。
                      </p>
                    </div>
                  )}

                  {status === "success" && (
                    <div className="mt-5 flex items-start gap-2 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                      <i className="ri-check-line mt-[2px] shrink-0 text-[14px] text-primary-700" />
                      <p className="text-[12px] leading-[1.85] text-primary-900">{statusMessage}</p>
                    </div>
                  )}

                  {status === "error" && (
                    <div className="mt-5 flex items-start gap-2 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
                      <i className="ri-error-warning-line mt-[2px] shrink-0 text-[14px] text-accent-600" />
                      <p className="text-[12px] leading-[1.85] text-accent-900">{statusMessage}</p>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className="mt-6 inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-6 py-3.5 text-[13.5px] font-semibold text-background-50 shadow-sm transition-all duration-200 hover:-translate-y-[2px] hover:bg-accent-600 hover:shadow-md disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0"
                  >
                    {status === "submitting" ? "送信中..." : "無料で質問する"}
                    <i className="ri-arrow-right-line text-[14px]" />
                  </button>

                  <p className="mt-3 text-center text-[11px] leading-[1.8] text-foreground-400">
                    無料の質問受付です。有料の個別相談ではありません。
                  </p>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}